/**
 * TAG-04 (#367): GA4's view_cart for the cart drawer.
 *
 * The 2026-09-20 audit found view_cart could never fire: shoppers review their cart in the drawer,
 * and nothing reported the drawer. This module pushes view_cart, in GA4's ecommerce shape, each
 * time the drawer opens. It covers the drawer only: /cart is already reported by the Google &
 * YouTube app, whose web pixel sends view_cart on Shopify's `cart_viewed`, which Shopify publishes
 * on /cart and never for the drawer. Reporting /cart here too would count it twice.
 *
 *   view_cart  ecommerce.currency, ecommerce.value, ecommerce.items[] (item_id, item_name,
 *              item_variant when the variant has a title, price, quantity)
 *
 * WHERE THE NUMBERS COME FROM. A same-origin read of Shopify's /cart.js under the locale root
 * (window.Shopify.routes.root), so /de/ reads /de/cart.js, with `cache: 'no-store'` so a cached
 * cart can never be reported. Prices arrive in cents and leave in currency units. Nothing about
 * the customer is copied: only the fields listed above. An empty cart pushes nothing.
 *
 * WHY item_id IS THE VARIANT ID, AND NOT THE APP'S FORMAT. The Google & YouTube app's own events
 * use shopify_<target country>_<product id>_<variant id>. Read on 2026-09-26 with plain GETs:
 * pixel 1860337960 (apiClientId 1780363) in the webPixelsConfigList of https://woopf.com/ has
 * scriptVersion a3321ca85cf5aaf6b57585fcd8d67b3a and config "target_country":"ZZ"; its code,
 * https://woopf.com/web-pixels@c8540278we853e1abpb62b2c4amde256306/app/web-pixel-1860337960@a3321ca85cf5aaf6b57585fcd8d67b3a~2/pixel.modern.js,
 * parses that config as `v=JSON.parse(a.settings.config)`, builds every item id with
 *
 *   N=e=>{var n;return"shopify_"+(v.target_country||"US")+"_"+String((n=e==null?void 0:e.product)
 *     ==null?void 0:n.id)+"_"+String(e==null?void 0:e.id)}
 *
 * and uses it in its cart_viewed handler as `items:...lines.map(o=>{...return{id:N(o.merchandise)`.
 * The ids are the plain numeric ones /cart.js carries (the same day's product page: product.id
 * "10115697606952", variant id "52169738289448"). The country, though, exists on the page only in
 * that pixel's config inside Shopify's web pixels loader script, and the PRIV-V02 (#150) guard in
 * tests/privacy/third-party.test.mjs forbids any woopf- module from touching the web pixel
 * configuration. Hard-coding "ZZ" is ruled out. Until that is decided, item_id stays the variant
 * id, so drawer items do not join the app's items in GA4 item reports; the event counts and
 * values are unaffected.
 *
 * WHEN. The drawer is `[data-wf-cart-drawer]` (sections/s-cart-modal.liquid); it is open while it
 * carries `is-open`, the class assets/section-cart-modal.js sets. A MutationObserver reports each
 * closed-to-open change once; re-renders while open are not views.
 *
 * WHY dataLayer. As in assets/woopf-lifecycle-events.js: snippets/woopf-consent-gate.liquid creates
 * window.dataLayer and boots GTM only once the Customer Privacy API allows analytics. Each payload
 * follows `{ ecommerce: null }`, which clears the previous ecommerce object, as GA4 asks.
 *
 * A failed read (offline, throttled, malformed) pushes nothing and throws nothing.
 *
 * Tests: tests/assets/commerce-events.test.mjs, tests/sections/tracking-markers.test.mjs.
 */

export const DRAWER = '[data-wf-cart-drawer]';
export const OPEN_CLASS = 'is-open';

const units = (cents) => (Number.isFinite(cents) ? Math.round(cents) / 100 : 0);

/**
 * @param {{ Shopify?: { routes?: { root?: string } } }} scope
 * @returns {string}
 */
export function cartUrl(scope) {
  const root = scope?.Shopify?.routes?.root;
  return `${typeof root === 'string' && root ? root : '/'}cart.js`;
}

/**
 * @param {any} cart the /cart.js response
 * @returns {object | null} the view_cart payload, or null for an unreadable or empty cart
 */
export function describeCart(cart) {
  if (!cart || typeof cart !== 'object') return null;
  if (typeof cart.currency !== 'string' || !Array.isArray(cart.items)) return null;
  if (cart.items.length === 0) return null;
  return {
    event: 'view_cart',
    ecommerce: {
      currency: cart.currency,
      value: units(cart.total_price),
      items: cart.items.map((item) => {
        const entry = {
          item_id: String(item.variant_id),
          item_name: String(item.product_title ?? ''),
          price: units(item.final_price ?? item.price),
          quantity: Number(item.quantity) || 0,
        };
        if (item.variant_title) entry.item_variant = String(item.variant_title);
        return entry;
      }),
    },
  };
}

/**
 * @returns {boolean} true when a payload was pushed
 */
export function pushViewCart(scope, cart) {
  const payload = describeCart(cart);
  if (!payload) return false;
  scope.dataLayer = scope.dataLayer || [];
  scope.dataLayer.push({ ecommerce: null });
  scope.dataLayer.push(payload);
  return true;
}

/**
 * @returns {Promise<boolean>} true when a payload was pushed
 */
export async function reportCart(scope) {
  try {
    const response = await scope.fetch(cartUrl(scope), {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response || !response.ok) return false;
    return pushViewCart(scope, await response.json());
  } catch {
    return false;
  }
}

/**
 * @returns {boolean} true when the drawer is being watched
 */
export function watchDrawer(scope) {
  const drawer = scope?.document?.querySelector?.(DRAWER);
  if (!drawer || typeof scope.MutationObserver !== 'function') return false;
  let open = drawer.classList.contains(OPEN_CLASS);
  const observer = new scope.MutationObserver(() => {
    const now = drawer.classList.contains(OPEN_CLASS);
    if (now && !open) reportCart(scope);
    open = now;
  });
  observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  return true;
}

export function start(scope) {
  const doc = scope?.document;
  if (!doc || typeof doc.querySelector !== 'function') return;
  watchDrawer(scope);
}

/* The auto-start. `typeof document` guards the Vitest run, which has no DOM at all. */
if (typeof document !== 'undefined' && typeof window !== 'undefined') start(window);
